/**
 * Job Types Report
 *
 * KPIs: total jobs, unique service types, top job type, revenue attributed
 * Chart: horizontal bar — jobs per service type (readable long labels)
 * Table: service | jobs | % of mix | revenue | avg job value
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from 'recharts';
import {
  Hammer, Layers, Award, DollarSign,
  ArrowLeft, RefreshCw, AlertCircle,
} from 'lucide-react';
import { Button }      from '../components/ui/button';
import { toast }       from 'sonner';
import axios           from 'axios';
import ChartShell      from '../features/reports/components/ChartShell';
import KpiCard         from '../features/reports/components/KpiCard';
import PeriodSelector  from '../features/reports/components/PeriodSelector';

const API = process.env.REACT_APP_BACKEND_URL;

async function getAuthToken() {
  const { getAuth } = await import('firebase/auth');
  const user = getAuth().currentUser;
  if (!user) throw new Error('Not authenticated');
  return user.getIdToken();
}

const fmtFull = (n) =>
  Number(n).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

// One color per service type
const TYPE_COLORS = [
  '#f26b1d', '#3b82f6', '#22c55e', '#a855f7', '#f59e0b',
  '#ef4444', '#14b8a6', '#ec4899', '#6366f1', '#84cc16',
  '#0ea5e9', '#fb923c', '#94a3b8',
];

const CustomBarTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-md p-3 text-xs">
      <p className="font-semibold text-gray-700 mb-1">{label}</p>
      <p style={{ color: payload[0]?.fill }}>{payload[0]?.value} job{payload[0]?.value !== 1 ? 's' : ''} completed</p>
    </div>
  );
};

// ── Mix % pill ────────────────────────────────────────────────────────────────

const MixPill = ({ pct }) => {
  const cls =
    pct >= 30 ? 'bg-orange-100 text-orange-700' :
    pct >= 15 ? 'bg-amber-100 text-amber-700' :
                'bg-gray-100 text-gray-500';
  return (
    <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${cls}`}>
      {pct}%
    </span>
  );
};

// ── Job Types table ───────────────────────────────────────────────────────────

const JobTypesTable = ({ jobTypes }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm" data-testid="job-types-table">
      <thead>
        <tr className="border-b border-gray-100">
          <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider py-2 pr-4">
            Service Type
          </th>
          <th className="text-right text-xs font-semibold text-gray-500 uppercase tracking-wider py-2 px-3">
            Jobs
          </th>
          <th className="text-right text-xs font-semibold text-gray-500 uppercase tracking-wider py-2 px-3">
            Mix
          </th>
          <th className="text-right text-xs font-semibold text-gray-500 uppercase tracking-wider py-2 px-3">
            Revenue
          </th>
          <th className="text-right text-xs font-semibold text-gray-500 uppercase tracking-wider py-2 pl-3">
            Avg Job Value
          </th>
        </tr>
      </thead>
      <tbody>
        {jobTypes.map((row, i) => (
          <tr
            key={row.service}
            className="border-b border-gray-50 hover:bg-gray-50 transition-colors"
            data-testid={`job-type-row-${row.service.toLowerCase().replace(/\s+/g, '-').replace(/\//g, '-')}`}
          >
            <td className="py-2.5 pr-4">
              <div className="flex items-center gap-2">
                <span
                  className="w-2.5 h-2.5 rounded-full shrink-0"
                  style={{ background: row.service === 'Unknown' ? '#cbd5e1' : TYPE_COLORS[i % TYPE_COLORS.length] }}
                />
                <span className={`font-medium ${row.service === 'Unknown' ? 'text-gray-400 italic' : 'text-gray-800'}`}>
                  {row.service}
                </span>
              </div>
            </td>
            <td className="text-right text-gray-700 font-semibold py-2.5 px-3">
              {row.jobs}
            </td>
            <td className="text-right py-2.5 px-3">
              <MixPill pct={row.pctOfMix} />
            </td>
            <td className="text-right font-semibold text-gray-800 py-2.5 px-3">
              {row.revenue > 0
                ? fmtFull(row.revenue)
                : <span className="text-gray-300">—</span>}
            </td>
            <td className="text-right text-gray-500 py-2.5 pl-3">
              {row.avgJobValue > 0
                ? fmtFull(row.avgJobValue)
                : <span className="text-gray-300">—</span>}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

// ── Page ──────────────────────────────────────────────────────────────────────

const ReportsJobTypes = () => {
  const [period,  setPeriod]  = useState('this_month');
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  const fetchData = useCallback(async (p) => {
    setLoading(true);
    setError(null);
    try {
      const token = await getAuthToken();
      const res   = await axios.get(`${API}/api/reports/job-types?period=${p}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to load job types report';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(period); }, [period, fetchData]);

  const periodLabel = {
    this_month: 'This Month',
    last_month: 'Last Month',
    last_3m:    'Last 3 Months',
    last_6m:    'Last 6 Months',
    this_year:  'This Year',
  }[period];

  const isEmpty = !loading && (data?.totalJobs ?? 0) === 0;

  // Top named type by job count
  const topType = data?.jobTypes?.find(t => t.service !== 'Unknown' && t.jobs > 0);
  // Whether the mix is truly diverse (3+ types)
  const isDiverse = (data?.uniqueServiceTypes ?? 0) >= 3;

  return (
    <div className="max-w-5xl mx-auto space-y-6 pb-12">

      {/* Header */}
      <div className="flex items-center justify-between pt-2">
        <div className="flex items-center gap-3">
          <Link
            to="/reports"
            className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Reports
          </Link>
          <span className="text-gray-300">/</span>
          <h1 className="text-xl font-bold text-gray-900">Job Types</h1>
        </div>
        <div className="flex items-center gap-3">
          <PeriodSelector value={period} onChange={setPeriod} />
          <Button
            variant="outline"
            size="sm"
            onClick={() => fetchData(period)}
            disabled={loading}
            data-testid="refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="job-types-kpi-grid">
        <KpiCard
          label={`Jobs Completed — ${periodLabel}`}
          value={loading ? '…' : String(data?.totalJobs ?? 0)}
          sub="Across all service types"
          icon={Hammer}
          iconColor="text-orange-500"
          iconBg="bg-orange-50"
          loading={loading}
          testId="kpi-total-jobs"
        />
        <KpiCard
          label="Service Types Active"
          value={loading ? '…' : String(data?.uniqueServiceTypes ?? 0)}
          sub={`Types with jobs in ${periodLabel.toLowerCase()}`}
          icon={Layers}
          iconColor="text-blue-500"
          iconBg="bg-blue-50"
          loading={loading}
          testId="kpi-service-types"
        />
        <KpiCard
          label="Top Job Type"
          value={loading ? '…' : (data?.topJobType ?? '—')}
          sub="Most performed service"
          icon={Award}
          iconColor="text-amber-500"
          iconBg="bg-amber-50"
          loading={loading}
          testId="kpi-top-job-type"
        />
        <KpiCard
          label="Revenue Attributed"
          value={loading ? '…' : fmtFull(data?.revenueAttributed ?? 0)}
          sub="Paid invoices for period jobs"
          icon={DollarSign}
          iconColor="text-green-600"
          iconBg="bg-green-50"
          loading={loading}
          testId="kpi-revenue-attributed"
        />
      </div>

      {/* Diversity callout */}
      {!loading && topType && isDiverse && (
        <div
          className="flex items-center gap-3 px-4 py-3 bg-orange-50 border border-orange-200 rounded-xl text-sm"
          data-testid="top-type-callout"
        >
          <Hammer className="w-4 h-4 text-orange-500 shrink-0" />
          <span className="text-orange-800">
            <span className="font-semibold">{topType.service}</span> makes up{' '}
            <span className="font-semibold">{topType.pctOfMix}%</span> of your work this period
            {topType.revenue > 0 && (
              <span> with {fmtFull(topType.revenue)} in attributed revenue</span>
            )}.
            {data.mostValuableType !== '—' && data.mostValuableType !== topType.service && (
              <span> <span className="font-semibold">{data.mostValuableType}</span> generates the most revenue per job.</span>
            )}
          </span>
        </div>
      )}

      {/* Horizontal bar chart — better for long service type labels */}
      <ChartShell
        title="Jobs by Service Type"
        subtitle={`Completed jobs per service category — ${periodLabel}`}
        loading={loading}
        empty={isEmpty}
      >
        <ResponsiveContainer width="100%" height={Math.max(200, (data?.byType?.length ?? 4) * 44)}>
          <BarChart
            data={data?.byType ?? []}
            layout="vertical"
            margin={{ top: 4, right: 24, left: 4, bottom: 4 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
            <XAxis
              type="number"
              allowDecimals={false}
              tick={{ fontSize: 11, fill: '#9ca3af' }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              type="category"
              dataKey="service"
              width={108}
              tick={{ fontSize: 11, fill: '#6b7280' }}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip content={<CustomBarTooltip />} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
            <Bar dataKey="jobs" name="Jobs" radius={[0, 4, 4, 0]} maxBarSize={28}>
              {(data?.byType ?? []).map((entry, i) => (
                <Cell
                  key={i}
                  fill={entry.service === 'Unknown' ? '#cbd5e1' : TYPE_COLORS[i % TYPE_COLORS.length]}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </ChartShell>

      {/* Breakdown table */}
      {!loading && !isEmpty && data?.jobTypes?.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
          <h3 className="text-sm font-semibold text-gray-900 mb-0.5">Service Breakdown</h3>
          <p className="text-xs text-gray-400 mb-4">
            Revenue = paid invoices linked to jobs completed this period ·
            Mix = % of total jobs in period
          </p>
          <JobTypesTable jobTypes={data.jobTypes} />
        </div>
      )}

      {isEmpty && (
        <div className="text-center py-12 text-gray-400" data-testid="job-types-empty">
          <Hammer className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p className="text-sm">No completed jobs found for this period.</p>
          <p className="text-xs mt-1">
            Service type data is captured when jobs are booked from leads.
          </p>
        </div>
      )}

      {error && (
        <div className="p-4 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700 flex gap-2">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          {error}
        </div>
      )}
    </div>
  );
};

export default ReportsJobTypes;
